import { EXTENSION_ID, MESSAGE_TYPES } from 'cfg/endpoints';
import { Loader } from 'components/Loader';
import { SERVICE_CALLS } from 'extension-helpers';
import { clearCache } from 'extension-helpers';
import { DEFAULT_LANGUAGE_SETTING, EMAIL_FREQUENCY_SETTING, PROSPECT_RECORDING_SETTING, SettingsUpdate } from "interfaces/users";
import { Typography } from 'interfaces/typography';
import { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { getServicesManager } from 'services';
import { RootState } from 'store';
import { updateUser, updateTeam } from 'lib/redux/users/slice';
import { useUserData } from 'lib/redux/users/user-data-hook';
import { SettingsSection, SettingRow, StandardContainer } from 'utils/CommonUIComponents';

export enum CHROME_SETTINGS {
  TRELLUS_MODE = 'Lite Mode',
  TRELLUS_SIDE = 'Popup to right',
  EMBEDDED_COACHING = 'Embedded coaching',
  EMBEDDED_COACHING_NOT_HIGHLIGHT = 'Highlight embedded coaching?',
}

export const CHROME_SETTINGS_TO_REQUEST_MESSAGE: {[k in CHROME_SETTINGS]: string} = {
  [CHROME_SETTINGS.TRELLUS_MODE]: MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_REQUEST_IS_LITE_MODE_SETTING,
  [CHROME_SETTINGS.TRELLUS_SIDE]: MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_REQUEST_POPUP_DIRECTION_SETTING,
  [CHROME_SETTINGS.EMBEDDED_COACHING]: MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_REQUEST_IS_EMBEDDED_COACHIG_ENABLED,
  [CHROME_SETTINGS.EMBEDDED_COACHING_NOT_HIGHLIGHT]: MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_REQUEST_IS_EMBEDDED_COACHING_NO_HIGHLIGHT,
}

export const RESPONSE_TO_CHROME_SETTING_VALUE: {[k in CHROME_SETTINGS]: (response: any) => boolean} = {
  [CHROME_SETTINGS.TRELLUS_MODE]: (response) => response && response['is_lite_mode_setting'],
  [CHROME_SETTINGS.TRELLUS_SIDE]: (response) => response &&  response['is_popup_to_right_setting'],
  [CHROME_SETTINGS.EMBEDDED_COACHING]: (response) => response &&  response['embedded_coaching'],
  [CHROME_SETTINGS.EMBEDDED_COACHING_NOT_HIGHLIGHT]: (response) => response && response['embedded_coaching_no_highlight'],
}

export enum BACKEND_SETTINGS {
  DEFAULT_AUDIO_SETTINGS = 'Persist audio recording',
  REALTIME_TRANSCRIPTION = 'Real-time transcription',
  TRANSCRIPTION_RECORDING = 'Persist transcription',
  LANGUAGE_SETTING = 'Default language',
  EMAIL_SETTINGS = 'Email settings',
  EMAIL_NOTIFICATION_SETTINGS = 'Notify on review changes?',
  DATA_SEARCH_ENABLED = 'Disable LinkedIn finder?',
  DISABLE_AUTODIALER = 'Disable Autodialer?',
}

// Add tab enum
export enum SETTINGS_TAB {
  PERSONAL = 'Personal Settings',
  TEAM = 'Team Settings',
}


const TEAM_ONLY_SETTINGS = [
  BACKEND_SETTINGS.REALTIME_TRANSCRIPTION,
  BACKEND_SETTINGS.TRANSCRIPTION_RECORDING,
  BACKEND_SETTINGS.DISABLE_AUTODIALER,
  BACKEND_SETTINGS.EMAIL_NOTIFICATION_SETTINGS,
]

const BOTH_LEVEL_SETTINGS = [
  BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS,
  BACKEND_SETTINGS.LANGUAGE_SETTING,
  BACKEND_SETTINGS.DATA_SEARCH_ENABLED,
]


// Tooltip descriptions for each setting
const SETTING_TOOLTIPS: {[key: string]: string} = {
  [CHROME_SETTINGS.TRELLUS_MODE]: 'Enable lite mode for a simplified coaching experience with fewer features',
  [CHROME_SETTINGS.TRELLUS_SIDE]: 'Position the coaching popup on the right side of your screen',
  [CHROME_SETTINGS.EMBEDDED_COACHING]: 'Show coaching insights directly embedded in your CRM interface',
  [CHROME_SETTINGS.EMBEDDED_COACHING_NOT_HIGHLIGHT]: 'Highlight embedded coaching elements to make them more visible',
  [BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS]: 'Choose which calls to automatically record by default',
  [BACKEND_SETTINGS.REALTIME_TRANSCRIPTION]: 'Enable live transcription during your calls for real-time insights',
  [BACKEND_SETTINGS.TRANSCRIPTION_RECORDING]: 'Save call transcripts automatically for future reference and analysis',
  [BACKEND_SETTINGS.LANGUAGE_SETTING]: 'Set the primary language for transcription and coaching insights',
  [BACKEND_SETTINGS.EMAIL_SETTINGS]: 'Configure how often you receive email updates and summaries',
  [BACKEND_SETTINGS.EMAIL_NOTIFICATION_SETTINGS]: 'Get notified via email when call review statuses change',
  [BACKEND_SETTINGS.DATA_SEARCH_ENABLED]: 'Disable automatic LinkedIn profile search and enrichment for prospects',
  [BACKEND_SETTINGS.DISABLE_AUTODIALER]: 'Completely disable autodialer functionality for your team (admin only)',
}


type Setting = {
  backendSetting?: BACKEND_SETTINGS
  chromeSetting?: CHROME_SETTINGS
}

type SettingSection = {
  settings: Setting[]
  label: string
}

const DEFAULT_LANGUAGE_SETTING_TO_LABEL: {[key in DEFAULT_LANGUAGE_SETTING]: string} = {
  [DEFAULT_LANGUAGE_SETTING.ENGLISH]: 'English',
  [DEFAULT_LANGUAGE_SETTING.SPANISH]: 'Spanish',
  [DEFAULT_LANGUAGE_SETTING.FRENCH]: "French",
  [DEFAULT_LANGUAGE_SETTING.MULTI]: "Auto-detect (English /Spanish)",
}

const EMAIL_FREQUENCY_SETTING_TO_LABEL: {[key in EMAIL_FREQUENCY_SETTING]: string} = {
  [EMAIL_FREQUENCY_SETTING.DAILY]: 'Daily',
  [EMAIL_FREQUENCY_SETTING.TWICE_A_WEEK]: 'Twice a week',
  [EMAIL_FREQUENCY_SETTING.WEEKLY]: 'Weekly',
  [EMAIL_FREQUENCY_SETTING.NONE]: 'Never'
}

const PROSPECT_RECORDING_SETTING_TO_LABEL: {[key in PROSPECT_RECORDING_SETTING]: string} = {
  [PROSPECT_RECORDING_SETTING.ALL]: 'All calls',
  [PROSPECT_RECORDING_SETTING.NONE]: 'No calls',
  [PROSPECT_RECORDING_SETTING.ONE_PARTY_ONLY]: 'All 1-party calls',
  [PROSPECT_RECORDING_SETTING.DIALER]: "Mirror dialer settings"
}

function GeneralSettings() {
  const dispatch = useDispatch()
  const { isAdmin, team } = useUserData()
  const user = useSelector((state: RootState) => state.user.value)
  const [chromeSettings, setChromeSettings] = useState<Map<CHROME_SETTINGS, boolean>>(new Map())
  const [activeTab, setActiveTab] = useState<SETTINGS_TAB>(SETTINGS_TAB.PERSONAL)
  const [updatingSettings, setUpdatingSettings] = useState<Set<BACKEND_SETTINGS>>(new Set())
  
  const isChromeEnabled = () => { return chrome !== undefined && chrome.runtime !== undefined && chrome.runtime.sendMessage !== undefined } 
  const [chromeEnabled, setChromeEnabled] = useState<boolean>(isChromeEnabled())
  
  const getChromeSettings = useCallback(async() => {
    if (!chromeEnabled) return
    for (const setting of Object.values(CHROME_SETTINGS)) {
      try {
        const response = await chrome.runtime.sendMessage(EXTENSION_ID, {
        type: CHROME_SETTINGS_TO_REQUEST_MESSAGE[setting],
        })
        if (!response) {
          setChromeEnabled(false)
          return
        }
        setChromeSettings((prev) => {
          const newMap = new Map(prev)
          newMap.set(setting, RESPONSE_TO_CHROME_SETTING_VALUE[setting](response))
          return newMap
        })
      } catch (error) {
        setChromeEnabled(false)
        return
      }
    }
  }, [chromeEnabled])

  useEffect(() => {
    getChromeSettings()
  }, [getChromeSettings])

  const updateChromeSetting = (setting: CHROME_SETTINGS, value: boolean) => { 
    let valueToSet = value
    switch (setting) {
      case CHROME_SETTINGS.TRELLUS_MODE:
        chrome.runtime.sendMessage(EXTENSION_ID, {
          'type': MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_SET_IS_LITE_MODE, 
          'is_lite_mode_setting': value, 
          'coaching_page': false
        })
        break
      case CHROME_SETTINGS.TRELLUS_SIDE:
        chrome.runtime.sendMessage(EXTENSION_ID, {
          'type': MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_SET_POPUP_DIRECTION_IS_RIGHT, 
          'is_popup_to_right_setting': value
        })
        break 
      case CHROME_SETTINGS.EMBEDDED_COACHING:
        chrome.runtime.sendMessage(EXTENSION_ID, {
          'type': MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_SET_IS_EMBEDDED_COACHING_ENABLED, 
          'embedded_coaching': value, 
        })
        break
      case CHROME_SETTINGS.EMBEDDED_COACHING_NOT_HIGHLIGHT:
        valueToSet = !value
        chrome.runtime.sendMessage(EXTENSION_ID, {
          'type': MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_SET_IS_EMBEDDED_COACHING_NO_HIGHLIGHT, 
          'embedded_coaching_no_highlight': valueToSet, 
        })
        break
    }
    setChromeSettings((prev) => {
      const newMap = new Map(prev)
      newMap.set(setting, valueToSet)
      return newMap
    })
  }
  
  const renderChromeSetting = (setting: CHROME_SETTINGS) => {
    let value = chromeSettings.get(setting) ?? false
    if (setting === CHROME_SETTINGS.EMBEDDED_COACHING_NOT_HIGHLIGHT) value = (!value) && (chromeSettings.get(CHROME_SETTINGS.EMBEDDED_COACHING) ?? false)
    return <SettingRow label={setting} type="toggle" value={value} setValue={(x) => updateChromeSetting(setting, x as boolean)} tooltip={SETTING_TOOLTIPS[setting]} />
  }

  const settingIsApplicableForTeam = (setting: BACKEND_SETTINGS) => {
    return TEAM_ONLY_SETTINGS.includes(setting) || BOTH_LEVEL_SETTINGS.includes(setting)
  }

  const updateSettingsResult = (updates: Map<BACKEND_SETTINGS, string | boolean>) => {
    // Mark settings as updating
    const updatingKeys = Array.from(updates.keys())
    setUpdatingSettings(prev => new Set([...prev, ...updatingKeys]))
    
    // Determine if this is a team-level update based on the active tab and admin status
    const isTeamUpdate = activeTab === SETTINGS_TAB.TEAM && isAdmin === true
    const settingUpdate: SettingsUpdate = {
      'is_team': isTeamUpdate && Array.from(updates.keys()).some((key) => settingIsApplicableForTeam(key as BACKEND_SETTINGS))
    }
    
    for (const [key, value] of updates) {
      switch (key) {
        case BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS: settingUpdate['default_prospect_recording'] = value as PROSPECT_RECORDING_SETTING; break
        case BACKEND_SETTINGS.REALTIME_TRANSCRIPTION: settingUpdate['default_prospect_streaming_transcription'] = value as PROSPECT_RECORDING_SETTING; break
        case BACKEND_SETTINGS.TRANSCRIPTION_RECORDING: settingUpdate['default_prospect_transcript_persistence'] = value as PROSPECT_RECORDING_SETTING; break
        case BACKEND_SETTINGS.LANGUAGE_SETTING: settingUpdate['default_language'] = value as DEFAULT_LANGUAGE_SETTING; break
        case BACKEND_SETTINGS.EMAIL_SETTINGS: settingUpdate['email_frequency'] = value as EMAIL_FREQUENCY_SETTING; break
        case BACKEND_SETTINGS.DATA_SEARCH_ENABLED: settingUpdate['linkedin_finder_disabled'] = value as boolean; break
        case BACKEND_SETTINGS.EMAIL_NOTIFICATION_SETTINGS: settingUpdate['notify_on_review_status_change'] = value as boolean; break
        case BACKEND_SETTINGS.DISABLE_AUTODIALER: settingUpdate['autodialer_disabled'] = value as boolean; break
    }
  }
  
  // Send update to backend
  getServicesManager().postSettings(settingUpdate).then((success) => {
    if (success) {
      // Update local Redux state on success
      if (isTeamUpdate) {
        // Get the current team state at the time of update
        const currentTeam = team
        if (currentTeam) {
          let updatedTeam = { ...currentTeam }
          for (const [key, value] of updates) {
            switch (key) {
              case BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS: 
                updatedTeam.default_prospect_recording = value as PROSPECT_RECORDING_SETTING; 
                break
              case BACKEND_SETTINGS.REALTIME_TRANSCRIPTION: 
                updatedTeam.default_prospect_streaming_transcription = value as PROSPECT_RECORDING_SETTING; 
                break
              case BACKEND_SETTINGS.TRANSCRIPTION_RECORDING: 
                updatedTeam.default_prospect_transcript_persistence = value as PROSPECT_RECORDING_SETTING; 
                break
              case BACKEND_SETTINGS.LANGUAGE_SETTING: 
                updatedTeam.default_language = value as DEFAULT_LANGUAGE_SETTING; 
                break
              case BACKEND_SETTINGS.DATA_SEARCH_ENABLED: 
                updatedTeam.linkedin_finder_disabled = value as boolean; 
                break
              case BACKEND_SETTINGS.EMAIL_NOTIFICATION_SETTINGS: 
                updatedTeam.notify_on_review_status_change = value as boolean; 
                break
              case BACKEND_SETTINGS.DISABLE_AUTODIALER: 
                updatedTeam.autodialer_disabled = value as boolean; 
                break
            }
          }
          dispatch(updateTeam(updatedTeam))
        }
      } else if (!isTeamUpdate && user) {
        let updatedUser = { ...user }
        for (const [key, value] of updates) {
          switch (key) {
            case BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS: 
              updatedUser.default_prospect_recording = value as PROSPECT_RECORDING_SETTING; 
              break
            case BACKEND_SETTINGS.REALTIME_TRANSCRIPTION: 
              updatedUser.default_prospect_streaming_transcription = value as PROSPECT_RECORDING_SETTING; 
              break
            case BACKEND_SETTINGS.TRANSCRIPTION_RECORDING: 
              updatedUser.default_prospect_transcript_persistence = value as PROSPECT_RECORDING_SETTING; 
              break
            case BACKEND_SETTINGS.LANGUAGE_SETTING: 
              updatedUser.default_language = value as DEFAULT_LANGUAGE_SETTING; 
              break
            case BACKEND_SETTINGS.EMAIL_SETTINGS: 
              updatedUser.email_frequency_setting = value as EMAIL_FREQUENCY_SETTING; 
              break
            case BACKEND_SETTINGS.DATA_SEARCH_ENABLED: 
              updatedUser.linkedin_finder_disabled = value as boolean; 
              break
            case BACKEND_SETTINGS.DISABLE_AUTODIALER: 
              updatedUser.autodialer_disabled = value as boolean; 
              break
          }
        }
        dispatch(updateUser(updatedUser))
      }
    }
  }).catch((error) => {
    console.error('Failed to update settings:', error)
  }).finally(() => {
    // Clear loading state for these settings
    setUpdatingSettings(prev => {
      const newSet = new Set(prev)
      updatingKeys.forEach(key => newSet.delete(key))
      return newSet
    })
  })
  
  clearCache(SERVICE_CALLS.USER_SETTING)
  }

  // Helper function to get the correct value based on context (personal vs team)
  const getSettingValue = (setting: BACKEND_SETTINGS): any => {
    const isTeamContext = activeTab === SETTINGS_TAB.TEAM
    
    switch (setting) {
      case BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS:
        return isTeamContext 
          ? team?.default_prospect_recording ?? PROSPECT_RECORDING_SETTING.ONE_PARTY_ONLY
          : user?.default_prospect_recording ?? PROSPECT_RECORDING_SETTING.ONE_PARTY_ONLY
      case BACKEND_SETTINGS.REALTIME_TRANSCRIPTION:
        return isTeamContext 
          ? team?.default_prospect_streaming_transcription ?? PROSPECT_RECORDING_SETTING.ALL
          : user?.default_prospect_streaming_transcription ?? PROSPECT_RECORDING_SETTING.ALL
      case BACKEND_SETTINGS.TRANSCRIPTION_RECORDING:
        return isTeamContext 
          ? team?.default_prospect_transcript_persistence ?? PROSPECT_RECORDING_SETTING.ALL
          : user?.default_prospect_transcript_persistence ?? PROSPECT_RECORDING_SETTING.ALL
      case BACKEND_SETTINGS.LANGUAGE_SETTING:
        return isTeamContext 
          ? team?.default_language ?? DEFAULT_LANGUAGE_SETTING.ENGLISH
          : user?.default_language ?? DEFAULT_LANGUAGE_SETTING.ENGLISH
      case BACKEND_SETTINGS.DATA_SEARCH_ENABLED:
        return isTeamContext 
          ? team?.linkedin_finder_disabled ?? false
          : user?.linkedin_finder_disabled ?? false
      case BACKEND_SETTINGS.EMAIL_SETTINGS:
        return user?.email_frequency_setting ?? EMAIL_FREQUENCY_SETTING.DAILY
      case BACKEND_SETTINGS.EMAIL_NOTIFICATION_SETTINGS:
        return team?.notify_on_review_status_change ?? false
      case BACKEND_SETTINGS.DISABLE_AUTODIALER:
        return isTeamContext 
          ? team?.autodialer_disabled ?? false
          : user?.autodialer_disabled ?? false
      default:
        return null
    }
  }

  const renderBackendSetting = (setting: BACKEND_SETTINGS) => {
    const isLoading = updatingSettings.has(setting)
    
    switch (setting) {
      case BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS:
      case BACKEND_SETTINGS.REALTIME_TRANSCRIPTION:
      case BACKEND_SETTINGS.TRANSCRIPTION_RECORDING:
        const value = getSettingValue(setting)
        return <SettingRow label={setting} 
          type="select" 
          options={Object.values(PROSPECT_RECORDING_SETTING).map((v) => {return {label: PROSPECT_RECORDING_SETTING_TO_LABEL[v], value: v}})}
          value={value}
          setValue={(value) => updateSettingsResult(new Map([[setting, value]]))} 
          tooltip={SETTING_TOOLTIPS[setting]}
          disabled={isLoading}
          loading={isLoading}
          />
      case BACKEND_SETTINGS.LANGUAGE_SETTING:
        return <SettingRow 
        label={setting} 
        type="select"
        options={Object.values(DEFAULT_LANGUAGE_SETTING).map((v) => {return {label: DEFAULT_LANGUAGE_SETTING_TO_LABEL[v], value: v}})}
        value={getSettingValue(setting)}
        setValue={(value) => updateSettingsResult(new Map([[setting, value]]))}
        tooltip={SETTING_TOOLTIPS[setting]}
        disabled={isLoading}
        loading={isLoading}
        />
      case BACKEND_SETTINGS.EMAIL_SETTINGS:
        return <SettingRow label={setting} 
          type="select"
          options={Object.values(EMAIL_FREQUENCY_SETTING).map((v) => {return {label: EMAIL_FREQUENCY_SETTING_TO_LABEL[v], value: v}})}
          value={getSettingValue(setting)}
          setValue={(value) => updateSettingsResult(new Map([[setting, value]]))}
          tooltip={SETTING_TOOLTIPS[setting]}
          disabled={isLoading}
          loading={isLoading}
          />
      case BACKEND_SETTINGS.DATA_SEARCH_ENABLED:
        return <SettingRow label={setting} type="toggle" 
        value={getSettingValue(setting)} 
        setValue={(value) => updateSettingsResult(new Map([[setting, value]]))}
        tooltip={SETTING_TOOLTIPS[setting]}
        disabled={isLoading}
        loading={isLoading}
        />
      case BACKEND_SETTINGS.EMAIL_NOTIFICATION_SETTINGS:
        return <SettingRow 
        label={setting} type="toggle" 
        value={getSettingValue(setting)} 
        setValue={(value) => updateSettingsResult(new Map([[setting, value]]))} 
        tooltip={SETTING_TOOLTIPS[setting]}
        disabled={isLoading}
        loading={isLoading}
      />
      case BACKEND_SETTINGS.DISABLE_AUTODIALER:
        return <SettingRow 
        label={setting} 
        type="toggle" 
        value={getSettingValue(setting)} 
        setValue={(value) => updateSettingsResult(new Map([[setting, value]]))}
        tooltip={SETTING_TOOLTIPS[setting]}
        disabled={isLoading}
        loading={isLoading}
        />
      default:
        return null
   }
  }

  const renderSetting = (setting: Setting) => {
    if (setting.chromeSetting) return renderChromeSetting(setting.chromeSetting)
    if (setting.backendSetting) return renderBackendSetting(setting.backendSetting)
    return null
  }

  const renderSettingSection = (section: SettingSection) => {
    return (
      <SettingsSection title={section.label}>
        {section.settings.map((setting, index) => (
          <div key={index}>
            {renderSetting(setting)}
          </div>
        ))}
      </SettingsSection>
    )
  }

  // Updated function to get settings sections based on active tab
  const getSettingsSection = (): SettingSection[] => {
    if (activeTab === SETTINGS_TAB.PERSONAL) {
      // Personal settings
      const personalSections: SettingSection[] = []
      
      // Extension settings (always personal)
      if (chromeEnabled) {
        personalSections.push({
          label: 'Extension Settings',
          settings: [
            { chromeSetting: CHROME_SETTINGS.TRELLUS_MODE },
            { chromeSetting: CHROME_SETTINGS.TRELLUS_SIDE },
            { chromeSetting: CHROME_SETTINGS.EMBEDDED_COACHING },
            { chromeSetting: CHROME_SETTINGS.EMBEDDED_COACHING_NOT_HIGHLIGHT },
          ]
        })
      }
      
      // Personal audio settings
      personalSections.push({
        label: 'Audio Settings',
        settings: [
          { backendSetting: BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS },
          { backendSetting: BACKEND_SETTINGS.LANGUAGE_SETTING },
        ]
      })

      // Personal data & search settings
      personalSections.push({
        label: 'Data & Search',
        settings: [
          { backendSetting: BACKEND_SETTINGS.DATA_SEARCH_ENABLED },
        ]
      })
      
      // Personal email settings  
      personalSections.push({
        label: 'Email Settings',
        settings: [
          { backendSetting: BACKEND_SETTINGS.EMAIL_SETTINGS },
        ]
      })
      
      return personalSections
    } else {
      // Team settings (only for admins)
      const teamSections: SettingSection[] = []
      
      teamSections.push({
        label: 'Team Audio Settings',
        settings: [
          { backendSetting: BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS },
          { backendSetting: BACKEND_SETTINGS.TRANSCRIPTION_RECORDING },
          { backendSetting: BACKEND_SETTINGS.REALTIME_TRANSCRIPTION },
          { backendSetting: BACKEND_SETTINGS.LANGUAGE_SETTING },
        ]
      })
      
      teamSections.push({
        label: 'Team Data & Tools',
        settings: [
          { backendSetting: BACKEND_SETTINGS.DATA_SEARCH_ENABLED },
          { backendSetting: BACKEND_SETTINGS.DISABLE_AUTODIALER },
        ]
      })

      teamSections.push({
        label: 'Team Notifications',
        settings: [
          { backendSetting: BACKEND_SETTINGS.EMAIL_NOTIFICATION_SETTINGS },
        ]
      })
      
      return teamSections
    }
  }

  // Render tab navigation
  const renderTabNavigation = () => {
    if (!isAdmin) return null // Don't show tabs for non-admins
    
    return (
      <div className="w-full mb-6">
        {/* Tab Navigation */}
        <div className="flex items-center justify-center mb-4">
          <nav className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setActiveTab(SETTINGS_TAB.PERSONAL)}
              className={`px-6 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === SETTINGS_TAB.PERSONAL
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Personal Settings
            </button>
            <button
              onClick={() => setActiveTab(SETTINGS_TAB.TEAM)}
              className={`px-6 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === SETTINGS_TAB.TEAM
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Team Settings
            </button>
          </nav>
        </div>
        
        {/* Context text */}
        <div className="text-center mb-1">
          <Typography variant="smallParagraph" color="#6b7280">
            {activeTab === SETTINGS_TAB.PERSONAL 
              ? "These settings apply only to your account" 
              : "These settings apply to all members of your team"}
          </Typography>
        </div>
      </div>
    )
  }

  // Render extension installation banner
  const renderExtensionBanner = () => {
    if (chromeEnabled) return null
    
    return (
      <div className="w-full mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <Typography variant="mediumParagraph" color="#1e40af" fontWeight="500">
                  Install the Chrome Extension
                </Typography>
                <Typography variant="smallParagraph" color="#3730a3">
                  Enable additional features including the dialer and enhanced coaching capabilities
                </Typography>
              </div>
            </div>
            <div className="flex-shrink-0">
              <a 
                href="https://app.trellus.ai/install"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                Install Extension
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!user || isAdmin === null) return <Loader />

  return (
    <StandardContainer>
      {renderExtensionBanner()}
      {renderTabNavigation()}
      {getSettingsSection().map((section, index) => (
        <div key={index}>
          {renderSettingSection(section)}
        </div>
      ))}
    </StandardContainer>
  )
}

export { GeneralSettings }